import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const NARRATION_BY_FACTOR_ID = Object.freeze({
  model: "planet_factor_model",
  hardware: "planet_factor_hardware",
  utilization: "planet_factor_utilization",
  cooling: "planet_factor_cooling",
  grid: "planet_factor_grid",
});
import { getAIExhibitCurrentStatus } from "../content/aiExhibitCurrentStatus";
import { getAIExhibitBatch4Copy } from "../content/aiExhibitBatch4Content";

export default function PlanetScreen({
  language="en",
  audioEnabled=false,
}){
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t=getAIExhibitBatch4Copy(language).planet;
  const env=getAIExhibitCurrentStatus(language).environment;
  const [factorId,setFactorId]=useState(t.factors[0].id);
  const [showPrompt,setShowPrompt]=useState(false);
  const factor=useMemo(()=>t.factors.find(x=>x.id===factorId)||t.factors[0],[factorId,t.factors]);
  return <View style={{padding:28}}>
    <Text style={{color:C.accent,fontSize:13,fontWeight:"900",letterSpacing:1.2}}>{t.eyebrow}</Text>
    <Text style={{color:C.text,fontSize:44,lineHeight:50,fontWeight:"900",marginTop:8}}>{t.title}</Text>
    <Text style={{color:C.muted,fontSize:18,lineHeight:27,marginTop:8,maxWidth:990}}>{t.subtitle}</Text>

    <AIExhibitNarrationPressable
      audioEnabled={audioEnabled}
      onPress={() => playNarration("planet_physical_infrastructure")}
      style={{flexDirection:"row",alignItems:"center",flexWrap:"wrap",gap:9,marginTop:24}}
    >{t.flow.map((x,i)=><React.Fragment key={x}><View style={{flex:1,minWidth:170,padding:16,borderRadius:15,borderWidth:1,borderColor:C.border,backgroundColor:i===t.flow.length-1?C.accentSoft:C.surfaceRaised}}><Text style={{color:C.text,fontSize:16,fontWeight:"900",textAlign:"center"}}>{x}</Text></View>{i<t.flow.length-1?<Text style={{color:C.accent,fontSize:24,fontWeight:"900"}}>→</Text>:null}</React.Fragment>)}</AIExhibitNarrationPressable>

    <View style={{flexDirection:"row",flexWrap:"wrap",gap:12,marginTop:18}}>{[
      ["planet_electricity","⚡",t.electricity,t.electricityText],
      ["planet_cooling_water","💧",t.cooling,t.coolingText],
      ["planet_materials","🧱",t.materials,t.materialsText],
      ["planet_ai_can_help","🌱",t.benefits,t.benefitsText],
    ].map(([narrationId,icon,title,text])=><AIExhibitNarrationPressable
      key={narrationId}
      audioEnabled={audioEnabled}
      onPress={() => playNarration(narrationId)}
      style={{flex:1,minWidth:235,padding:18,borderRadius:17,borderWidth:1,borderColor:C.border,backgroundColor:C.surfaceRaised}}
    ><Text style={{fontSize:29}}>{icon}</Text><Text style={{color:C.text,fontSize:19,fontWeight:"900",marginTop:9}}>{title}</Text><Text style={{color:C.muted,fontSize:14,lineHeight:22,marginTop:7}}>{text}</Text></AIExhibitNarrationPressable>)}</View>

    <AIExhibitNarrationPressable
      audioEnabled={audioEnabled}
      onPress={() => playNarration("planet_data_center_electricity_scale")}
      style={{marginTop:18,padding:22,borderRadius:18,borderWidth:1,borderColor:C.accent,backgroundColor:C.accentSoft}}
    >
      <Text style={{color:C.accent,fontSize:13,fontWeight:"900"}}>{t.currentTitle.toUpperCase()}</Text>
      <View style={{flexDirection:"row",alignItems:"flex-end",gap:18,flexWrap:"wrap",marginTop:10}}><View><Text style={{color:C.text,fontSize:39,fontWeight:"900"}}>~{env.electricity2024TWh} TWh</Text><Text style={{color:C.muted,fontSize:14}}>2024</Text></View><Text style={{color:C.accent,fontSize:30,fontWeight:"900",marginBottom:8}}>→</Text><View><Text style={{color:C.text,fontSize:39,fontWeight:"900"}}>&gt;{env.electricity2030TWh.toLocaleString()} TWh</Text><Text style={{color:C.muted,fontSize:14}}>2030 base case</Text></View></View>
      <Text style={{color:C.text,fontSize:16,lineHeight:25,marginTop:13}}>{env.statement}</Text>
      <Text style={{color:C.warning,fontSize:15,lineHeight:23,fontWeight:"900",marginTop:10}}>{t.allDataCenters}</Text>
      <Text style={{color:C.muted,fontSize:12,marginTop:8}}>{env.sourceLabel} · Reviewed {env.lastReviewed}</Text>
    </AIExhibitNarrationPressable>

    <Text style={{color:C.text,fontSize:20,fontWeight:"900",marginTop:22}}>{t.factorsTitle}</Text>
    <View style={{flexDirection:"row",flexWrap:"wrap",gap:9,marginTop:10}}>{t.factors.map(x=>{const active=x.id===factorId;return <Pressable key={x.id} onPress={()=>{
      setFactorId(x.id);
      playNarration(NARRATION_BY_FACTOR_ID[x.id]);
    }} style={({pressed})=>({flex:1,minWidth:165,minHeight:74,padding:12,borderRadius:14,borderWidth:active?2:1,borderColor:active?C.accent:C.border,backgroundColor:active||pressed?C.accentSoft:C.surfaceRaised,flexDirection:"row",alignItems:"center",gap:8})}><Text style={{fontSize:23}}>{x.icon}</Text><Text style={{color:C.text,fontSize:14,fontWeight:"900",flexShrink:1}}>{x.title}</Text></Pressable>})}</View>
    <View style={{marginTop:12,padding:17,borderRadius:15,borderWidth:1,borderColor:C.border,backgroundColor:C.surfaceRaised}}><Text style={{color:C.text,fontSize:18,fontWeight:"900"}}>{factor.icon} {factor.title}</Text><Text style={{color:C.muted,fontSize:15,lineHeight:23,marginTop:7}}>{factor.text}</Text></View>

    <Pressable onPress={()=>{
      const nextShowPrompt=!showPrompt;
      setShowPrompt(nextShowPrompt);
      if(nextShowPrompt) playNarration("planet_no_single_prompt_number");
    }} style={({pressed})=>({marginTop:18,padding:17,borderRadius:15,borderWidth:1,borderColor:C.warning,backgroundColor:pressed||showPrompt?C.accentSoft:C.surfaceRaised})}><Text style={{color:C.text,fontSize:18,lineHeight:25,fontWeight:"900"}}>{showPrompt?"▾":"▸"} {t.noPrompt}</Text>{showPrompt?<Text style={{color:C.text,fontSize:15,lineHeight:24,marginTop:10}}>{t.noPromptAnswer}</Text>:null}</Pressable>
    <AIExhibitNarrationPressable
      audioEnabled={audioEnabled}
      onPress={() => playNarration("planet_balance_takeaway")}
      style={{marginTop:16,padding:18,borderRadius:16,borderLeftWidth:5,borderWidth:1,borderColor:C.border,borderLeftColor:C.success,backgroundColor:C.surfaceRaised}}
    ><Text style={{color:C.text,fontSize:17,lineHeight:27,fontWeight:"800"}}>{t.balance}</Text></AIExhibitNarrationPressable>
  </View>;
}
